import { isUndefined } from "lodash";
import { useEffect, useState } from "react";
import { useFetch } from "../../../../hooks/useFetch";
import { Sensor, SensorSlotData, SensorType } from "../../../../types/types";
import EBaseLineChart from "../../../../ui/Charts/eBaseLineChart";
import EBaseBarChart from "../../../../ui/Charts/eBaseBarChart";
import EBaseEventChart from "../../../../ui/Charts/eBaseEventChart";
import Spinner from "../../../../ui/Spinner/Spinner";
import { DateRange } from "react-day-picker";
import { useAppStore } from "../../../../stores/RootStore";
import _ from "lodash";
import { DateTime } from "luxon";
import BarMilkChart from "@/ui/Charts/BarMilkChart";


type Props = {
  sensor: SensorSlotData;
  sensorId: string;
  selectedDate: DateRange;
  showRfid?: boolean;
};

const DeviceSensorData = ({ sensor, sensorId, selectedDate, showRfid = true }: Props) => {
  const {triggers} = useAppStore()
  const [sensorType, setSensorType] = useState<SensorType>(sensor.sensor_type)
  const [sensorData, setSensorData] = useState<any>()
  const [loading, setLoading] = useState(true)
  const [dates, setDates] = useState({from: selectedDate?.from!, to: selectedDate?.to!})
  const [trigger, setTrigger] = useState<{ min: number | null | undefined, max: number | null | undefined }>({ min: null, max: null })

  const [
    {
      response: itemResponse,
      isLoading: itemIsLoading,
      error: itemNetworkError,
    },
    doFetch,
  ] = useFetch(
    `/devices/sensors/${sensorId}/received_data?from=${DateTime.fromJSDate(dates.from!).toISO()}&to=${DateTime.fromJSDate(dates.to!).toISO()}`,
  );
  
  const [{ response: oneMinuteResponse }, doOneMinuteFetch] = useFetch(
    `/devices/sensors/${sensorId}/received_data?from=${DateTime.fromJSDate(dates.from!).toISO()}&to=${DateTime.fromJSDate(dates.to!).toISO()}&capacity=one_minute&scalable_by=sum`,
  );

  const fetchData = () => {
    switch (sensorType?.chart_type) {
      case "milk_chart":
        doOneMinuteFetch();
        break;
      default:
        doFetch();
        break;
    }
  };

  const getResponseData = () => {
    switch (sensorType?.chart_type) {
      case "milk_chart":
        return oneMinuteResponse;

      default:
        return itemResponse;
    }
  };

  useEffect(() => {
    let triggersForSensor = triggers?.filter(
      (t) => t.attributes.sensor_id === Number(sensorId),
    );
    let mi = _.min(triggersForSensor?.filter(t => t.attributes.type === "Sites::Triggers::ValueLessThan").map(t => t.attributes.value_less_than))
    let ma = _.max(triggersForSensor?.filter(t => t.attributes.type === "Sites::Triggers::ValueMoreThan").map(t => t.attributes.value_more_than))
    setTrigger({
      min: mi,
      max: ma
    })
  }, [triggers])

  useEffect(() => {
    if (!selectedDate.to || !selectedDate.from || isUndefined(selectedDate.from)) {
      return;
    }
    setLoading(true)
    setDates({ from: selectedDate?.from!, to: selectedDate?.to! });
  }, [selectedDate.from, selectedDate.to]);

  useEffect(() => {
    if (!dates.to || !dates.from || isUndefined(dates.from)) {
      return;
    }
    fetchData();
  }, [sensorId, dates.from, dates.to]);


  useEffect(() => {
    //if(itemIsLoading) { return }
    const dataResponse = getResponseData();
    if (!dataResponse) return
    setSensorType(dataResponse?.data.attributes.sensor_type);
    setSensorData(dataResponse?.data.attributes.data);
    setLoading(false);
  }, [oneMinuteResponse, itemResponse]);



  function loadChart(sensor_type: SensorType) {
    let chart_type_var;
    if (sensor_type.chart_type == "super_chart")
      chart_type_var = sensor_type.data_key_name;
    else chart_type_var = sensor_type.chart_type;

    switch (chart_type_var) {
      case "rfid_chart":
      case "rfid":
      case "rfid_hold":
      case "rfid_in":
      case "rfid_out":
        return showRfid ? (
          <EBaseEventChart
            data={sensorData}
            type={sensorType?.data_key_name!}
            title={sensorType?.name!}
            selectedDate={selectedDate}
            onChangeDates={setDates}
            loading={loading}
          />
        ) : (
          <></>
        );
      case "ac_rms_chart":
      case "ac_rms":
        return (
          <EBaseBarChart
            data={sensorData}
            type={sensorType?.data_key_name!}
            title={sensorType?.name!}
            selectedDate={selectedDate}
            onChangeDates={setDates}
            loading={loading}
            idleLevel={sensor.idle_level}
            withStat={false}
            trigger={trigger}
          />
        );
      case "i_chart":
      case "i":
        return (
          <EBaseBarChart
            data={sensorData}
            type={sensorType?.data_key_name!}
            title={sensorType?.name!}
            selectedDate={selectedDate}
            onChangeDates={setDates}
            loading={loading}
            withStat={true}
            trigger={trigger}
          />
        );
      case "milk_chart":
        return (
          <BarMilkChart
            data={sensorData}
            type={sensorType?.data_key_name!}
            title={sensorType?.name!}
            selectedDate={selectedDate}
            onChangeDates={setDates}
            loading={loading}
            withStat={true}
            trigger={trigger}
          />
        );
      default:
        return (
          <EBaseLineChart
            data={sensorData}
            type={sensorType?.data_key_name!}
            title={sensorType?.name!}
            selectedDate={selectedDate}
            onChangeDates={setDates}
            loading={loading}
            idleLevel={sensor.idle_level}
          />
        );
    }
  }


  return (
    <div className="flex flex-col space-y-4 rounded">
      {sensorData && loadChart(sensorType)}
    </div>
  );
};

export default DeviceSensorData;
